import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Home, Calendar, Wand2, FileText, Pencil, User, Mail, Phone, ArrowUpRight, Signature, XCircle, Clock as ClockIcon, AlertTriangle, Plus, Send, Download, Loader2, X, Search, Trash2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TransactionTimeline from "../components/TransactionTimeline";
import TransactionEdit from "../components/TransactionEdit";
import PdfFillDialog from "../components/PdfFillDialog";
import DocPreviewModal from "../components/DocPreviewModal";
import { SignatureChip } from "../components/SignatureChip";
import { EmailLink, PhoneLink } from "../components/ContactLinks";

const NOT_PROVIDED = <span className="text-slate-400 italic">Not provided</span>;

/**
 * Renders one labelled cell in the info-panel grid. Falls back to a
 * subdued "Not provided" placeholder when the field is blank so agents
 * can tell missing data apart from a zero.
 */
function InfoCell({ label, value, testId, mono = false }) {
  const empty = value === null || value === undefined || value === "" || value === false;
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 text-sm text-slate-900 break-words ${mono ? "tabular-nums font-medium" : ""}`}>
        {empty ? NOT_PROVIDED : value}
      </div>
    </div>
  );
}

function PanelShell({ title, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 sm:px-6 py-3 border-b border-slate-200">
        <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-700">{title}</h3>
      </div>
      <div className="px-5 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
        {children}
      </div>
    </section>
  );
}

/**
 * Transaction Details info panels. Every field the Create + Edit forms
 * collect renders here — grouped Property Info · Financials · Key
 * Dates · Team & Counterparty · Notes. Empty values show "Not provided"
 * so agents can distinguish missing data from real zeros.
 *
 * Layout is a responsive 1 / 2 / 3-column grid inside each panel so
 * mobile stacks and desktop stays scannable side-by-side.
 */
function TransactionInfoPanels({ tx }) {
  const fmtMoney = (n) => (n == null ? null : `$${Number(n).toLocaleString()}`);
  const fmtPct = (n) => (n == null ? null : `${Number(n).toFixed(2)}%`);
  return (
    <div className="space-y-4 mb-6" data-testid="tx-info-panels">
      <PanelShell title="Property Info">
        <InfoCell label="Property address" value={tx.property_address} testId="info-address" />
        <InfoCell label="Side" value={tx.side ? tx.side.charAt(0).toUpperCase() + tx.side.slice(1) : null} testId="info-side" />
        <InfoCell label="Status" value={tx.status ? tx.status.replace(/_/g, " ") : null} testId="info-status" />
      </PanelShell>

      <PanelShell title="Financials">
        <InfoCell label="Purchase price" value={fmtMoney(tx.purchase_price)} testId="info-price" mono />
        <InfoCell label="Earnest money" value={fmtMoney(tx.earnest_money)} testId="info-earnest" mono />
        <InfoCell label="Commission rate" value={fmtPct(tx.commission_rate)} testId="info-comm-rate" mono />
        <InfoCell label="Commission amount" value={fmtMoney(tx.commission_amount)} testId="info-comm-amt" mono />
      </PanelShell>

      <PanelShell title="Key Dates">
        <InfoCell label="Acceptance date" value={tx.acceptance_date} testId="info-acceptance" mono />
        <InfoCell label="Close of escrow" value={tx.close_of_escrow_date} testId="info-coe" mono />
        <InfoCell
          label="Deadlines confirmed"
          value={tx.deadlines_confirmed_at ? new Date(tx.deadlines_confirmed_at).toLocaleDateString() : null}
          testId="info-deadlines-confirmed"
          mono
        />
      </PanelShell>

      <PanelShell title="Team & Counterparty">
        <InfoCell label="Lender" value={tx.lender_name} testId="info-lender" />
        <InfoCell label="Title company" value={tx.title_company} testId="info-title" />
        <InfoCell label="Escrow officer" value={tx.escrow_officer} testId="info-escrow" />
        <InfoCell label="Counterparty name" value={tx.counterparty_name} testId="info-cp-name" />
        <InfoCell
          label="Counterparty email"
          value={tx.counterparty_email ? <EmailLink email={tx.counterparty_email} testId="info-cp-email-link" /> : null}
          testId="info-cp-email"
        />
        <InfoCell
          label="Counterparty phone"
          value={tx.counterparty_phone ? <PhoneLink phone={tx.counterparty_phone} testId="info-cp-phone-link" /> : null}
          testId="info-cp-phone"
        />
      </PanelShell>

      {/* Notes gets its own full-width panel — it's free-text so it
          doesn't fit the labelled grid cleanly. */}
      <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="tx-info-notes-panel">
        <div className="px-5 sm:px-6 py-3 border-b border-slate-200">
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-700">Notes</h3>
        </div>
        <div className="px-5 sm:px-6 py-5 text-sm text-slate-800 whitespace-pre-wrap break-words">
          {tx.notes && tx.notes.trim() ? tx.notes : NOT_PROVIDED}
        </div>
      </section>
    </div>
  );
}

const SEVERITY_STYLES = {
  high: "border-red-300 bg-red-50 text-red-800",
  medium: "border-amber-300 bg-amber-50 text-amber-800",
  normal: "border-slate-300 bg-slate-50 text-slate-700",
};

const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-100 text-blue-800",
  met: "bg-emerald-100 text-emerald-800",
  missed: "bg-red-100 text-red-800",
  waived: "bg-slate-100 text-slate-500",
};

export default function TransactionDetail() {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/transactions/${id}`);
    setTx(data);
  };
  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [id]);

  const setStatus = async (deadlineId, status) => {
    await api.patch(`/transactions/${id}/deadlines/${deadlineId}`, { status });
    toast.success("Updated");
    load();
  };

  const confirmAll = async () => {
    await api.post(`/transactions/${id}/confirm-deadlines`);
    toast.success("Deadlines confirmed against contract");
    load();
  };

  const updateDate = async (deadlineId, due_date) => {
    await api.patch(`/transactions/${id}/deadlines/${deadlineId}`, { due_date });
    toast.success("Date updated");
    load();
  };

  if (!tx) return <div className="text-sm text-slate-500">Loading…</div>;

  const daysUntil = (d) => Math.round((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div>
      <Link to="/transactions" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-tx">
        <ArrowLeft className="h-4 w-4" /> Back to transactions
      </Link>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-6">
        {tx.property_hero_url && (
          <div className="relative">
            <img
              src={tx.property_hero_url}
              alt={tx.property_address || ""}
              className="w-full h-56 sm:h-64 object-cover"
              data-testid="tx-property-hero"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />
            {(tx.property_beds || tx.property_baths || tx.property_sqft) && (
              <div className="absolute bottom-3 left-6 text-xs font-semibold text-white/95 tabular-nums flex items-center gap-3 drop-shadow-md" data-testid="tx-property-specs">
                {tx.property_beds && <span>{tx.property_beds} bd</span>}
                {tx.property_baths && <span>{tx.property_baths} ba</span>}
                {tx.property_sqft && <span>{Number(tx.property_sqft).toLocaleString()} sqft</span>}
              </div>
            )}
          </div>
        )}
        <div className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
              <Home className="h-3.5 w-3.5" /> {tx.contract_form.replace(/_/g, " ")} · {tx.side}
            </div>
            <h1 className="mt-2 font-heading text-2xl font-bold tracking-tighter text-slate-900" data-testid="tx-address">{tx.property_address || "—"}</h1>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div><div className="text-slate-900 font-semibold capitalize mt-0.5">{tx.status.replace(/_/g,' ')}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Price</div><div className="text-slate-900 font-semibold tabular-nums mt-0.5">{tx.purchase_price ? `$${tx.purchase_price.toLocaleString()}` : "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Acceptance</div><div className="text-slate-900 font-semibold tabular-nums mt-0.5">{tx.acceptance_date || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Close</div><div className="text-slate-900 font-semibold tabular-nums mt-0.5">{tx.close_of_escrow_date || "—"}</div></div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="tx-edit-btn">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            {!tx.deadlines_confirmed_at && tx.deadlines.length > 0 && (
              <button onClick={confirmAll} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="confirm-deadlines-btn">
                <CheckCircle2 className="h-4 w-4" /> Confirm against contract
              </button>
            )}
          </div>
        </div>
        </div>
      </div>

      {tx.client ? (
        <Link
          to={`/clients?highlight=${tx.client.id}`}
          className="group block rounded-lg border border-slate-200 bg-white p-5 mb-6 hover:border-slate-400 hover:shadow-sm transition"
          data-testid="tx-client-card"
          title="Open this client's full profile"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 shrink-0 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" aria-hidden="true">
              <User className="h-5 w-5 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Client Information</div>
              <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="font-heading text-lg font-bold text-slate-900 group-hover:underline" data-testid="tx-client-name">{tx.client.full_name}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 group-hover:text-slate-900">
                  View profile <ArrowUpRight className="h-3 w-3" />
                </span>
              </div>
              <div className="mt-2 flex items-center gap-x-5 gap-y-1 text-sm text-slate-600 flex-wrap">
                <EmailLink email={tx.client.email} testId="tx-client-email" stop />
                <PhoneLink phone={tx.client.phone} testId="tx-client-phone" stop />
              </div>
            </div>
          </div>
        </Link>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 mb-6 flex items-center gap-3" data-testid="tx-client-empty">
          <User className="h-5 w-5 text-slate-400" />
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Client Information</div>
            <div className="text-sm text-slate-600 mt-0.5">
              No client attached yet. <Link to="/clients" className="font-semibold text-slate-900 hover:underline">Add or link a client →</Link>
            </div>
          </div>
        </div>
      )}

      <TransactionInfoPanels tx={tx} />

      <TransactionTimeline tx={tx} />

      <SigningActivityCard txId={id} />

      <TransactionDocuments txId={id} />

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-500" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Contract deadlines</h2>
          {tx.deadlines_confirmed_at && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded ml-2">Confirmed</span>}
        </div>
        {tx.deadlines.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500">No deadlines yet. Set an acceptance date on this transaction to generate the full compliance set.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tx.deadlines.map(d => {
              const days = daysUntil(d.due_date);
              const overdue = days < 0 && d.status !== "met" && d.status !== "waived";
              return (
                <li key={d.id} className="px-6 py-4 flex items-baseline justify-between gap-4 hover:bg-slate-50" data-testid={`deadline-${d.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${SEVERITY_STYLES[d.severity] || SEVERITY_STYLES.normal}`}>{d.severity}</span>
                      <span className="font-semibold text-slate-900 text-sm">{d.label}</span>
                      <span className="text-xs text-slate-500">· {d.basis}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <input type="date" value={d.due_date} onChange={e => updateDate(d.id, e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs tabular-nums" data-testid={`deadline-date-${d.key}`} />
                    {overdue && <span className="text-xs font-semibold text-red-700">{Math.abs(days)}d overdue</span>}
                    {!overdue && d.status !== "met" && d.status !== "waived" && <span className="text-xs text-slate-500 tabular-nums">{days === 0 ? "today" : days > 0 ? `in ${days}d` : ""}</span>}
                    <select value={d.status} onChange={e => setStatus(d.id, e.target.value)} className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_STYLES[d.status] || "bg-slate-100"}`} data-testid={`deadline-status-${d.key}`}>
                      <option value="pending">pending</option>
                      <option value="confirmed">confirmed</option>
                      <option value="met">met</option>
                      <option value="missed">missed</option>
                      <option value="waived">waived</option>
                    </select>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {editing && <TransactionEdit tx={tx} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

// ── Signing Activity ─────────────────────────────────────────────────────
// Every DocuSign signing-session event the client generated (opened /
// completed / cancelled / expired), newest first. Empty state renders a
// helpful hint so agents know what to expect once the client signs.

const SIGNING_EVENT_META = {
  opened:            { label: "Opened signing session",  tone: "sky",     Icon: Signature },
  signing_complete:  { label: "Signed",                  tone: "emerald", Icon: CheckCircle2 },
  viewing_complete:  { label: "Viewed & signed",         tone: "emerald", Icon: CheckCircle2 },
  cancel:            { label: "Cancelled signing",       tone: "slate",   Icon: XCircle },
  decline:           { label: "Declined to sign",        tone: "rose",    Icon: XCircle },
  session_timeout:   { label: "Session timed out",       tone: "amber",   Icon: ClockIcon },
  ttl_expired:       { label: "Signing link expired",    tone: "amber",   Icon: ClockIcon },
  exception:         { label: "DocuSign error",          tone: "rose",    Icon: AlertTriangle },
};

const SIGNING_TONE = {
  sky:     "bg-sky-500",
  emerald: "bg-emerald-500",
  amber:   "bg-amber-500",
  rose:    "bg-rose-500",
  slate:   "bg-slate-400",
};

function SigningActivityCard({ txId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stopped = false;
    const load = () => api.get(`/transactions/${txId}/signing-events`)
      .then(({ data }) => { if (!stopped) setRows(data || []); })
      .catch(() => {})
      .finally(() => { if (!stopped) setLoading(false); });
    load();
    // Poll every 30s so the agent sees "just opened" events without a refresh.
    const id = setInterval(load, 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [txId]);

  if (loading) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="signing-activity-card">
      <div className="flex items-center gap-2 mb-1">
        <Signature className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Signing activity</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">Every time the client opens, signs, declines, or lets a signing link expire — logged here.</p>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="signing-activity-empty">
          Nothing yet. Once you send a document for signature and the client opens it, events will show up here.
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-3 space-y-3">
          {rows.map(r => {
            const m = SIGNING_EVENT_META[r.event] || { label: r.event, tone: "slate", Icon: Signature };
            const dot = SIGNING_TONE[m.tone];
            const Icon = m.Icon;
            const when = new Date(r.created_at);
            return (
              <li key={r.id} className="pl-6 relative" data-testid={`signing-event-${r.id}`}>
                <span className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full ring-2 ring-white ${dot} flex items-center justify-center`}>
                  <Icon className="h-2.5 w-2.5 text-white" />
                </span>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="text-sm text-slate-900">
                    <span className="font-semibold">{m.label}</span>
                    <span className="text-slate-500"> · {r.document_name}</span>
                  </div>
                  <time className="text-[11px] text-slate-500 tabular-nums" dateTime={r.created_at}>
                    {when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}



// ── Transaction Documents ────────────────────────────────────────────────
// The "workshop" for this deal. Docs here are deal-scoped copies pulled
// from the Vault (or uploaded directly). Every action lives here so the
// Vault stays a read-only library:
//   • Add from Vault      → creates a fresh copy on this transaction
//   • Smart Fill          → transaction-data auto-fill, signature fields
//                           locked (DocuSign owns those)
//   • Send to DocuSign    → routes to the linked portal client

function TransactionDocuments({ txId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fillDoc, setFillDoc] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Click a doc row → open the shared PDF preview modal (blank templates,
  // Smart-Fill outputs, and countersigned copies all render through the
  // same iframe route). Non-PDFs fall back to the browser tab.
  const [previewDoc, setPreviewDoc] = useState(null); // { id, name }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents", { params: { transaction_id: txId } });
      setDocs(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [txId]);

  const download = async (id) => {
    const { data } = await api.get(`/documents/${id}/download`);
    window.open(data.url, "_blank");
  };

  const sendForSignature = async (id) => {
    setSendingId(id);
    try {
      await api.post("/docusign/send-auto", { document_id: id });
      toast.success("Sent to the client's portal — they'll see a Sign now button and get an in-app notification.");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send for signature";
      toast.error(detail);
    } finally {
      setSendingId(null);
    }
  };

  const deleteDoc = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
      setConfirmDelete(null);
    }
  };

  const isPdf = (d) => (d.mime_type || "").includes("pdf") || (d.name || "").toLowerCase().endsWith(".pdf");

  // Hide auto-filed signed copies from the top level — they render nested
  // under their original so agents see the "Original → Signed" chain in
  // one spot instead of scanning the list twice.
  const docsById = Object.fromEntries(docs.map(d => [d.id, d]));
  const signedCopyIds = new Set(docs.map(d => d.signed_document_id).filter(Boolean));
  const topLevelDocs = docs.filter(d => !signedCopyIds.has(d.id));

  const renderSignedCopy = (signedId) => {
    const s = docsById[signedId];
    if (!s) return null;
    return (
      <div
        className="mt-2 ml-6 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-center gap-2"
        data-testid={`tx-doc-signed-${s.id}`}
      >
        <CornerDownRight className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
        <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-emerald-900 truncate">{s.name}</div>
          <div className="text-[11px] text-emerald-800/80 mt-0.5">
            Signed copy · filed {new Date(s.created_at).toLocaleDateString()}
          </div>
        </div>
        <button
          type="button"
          onClick={() => download(s.id)}
          className="text-emerald-700 hover:text-emerald-900 p-1"
          title="Download signed copy"
          data-testid={`tx-doc-signed-dl-${s.id}`}
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="tx-documents">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-500" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Documents</h2>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white"
          style={{ background: "var(--primary-hex)" }}
          data-testid="tx-add-from-vault-btn"
        >
          <Plus className="h-4 w-4" /> Add from Vault
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Deal-scoped copies of your Vault templates. Smart Fill and DocuSign live here — the Vault stays untouched.</p>

      {loading && <div className="text-sm text-slate-500 py-4">Loading documents…</div>}
      {!loading && topLevelDocs.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="tx-docs-empty">
          <FileText className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-2 font-semibold text-slate-900 text-sm">No documents on this deal yet</p>
          <p className="mt-1 text-xs text-slate-500">Click <b>Add from Vault</b> above to pull in a contract template.</p>
        </div>
      )}

      <ul className="divide-y divide-slate-100">
        {topLevelDocs.map(d => {
          const armed = confirmDelete === d.id;
          return (
            <li key={d.id} className="py-3" data-testid={`tx-doc-${d.id}`}>
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                <button
                  type="button"
                  onClick={() => isPdf(d)
                    ? setPreviewDoc({ id: d.id, name: d.name })
                    : download(d.id)}
                  className="flex-1 min-w-0 text-left hover:opacity-80"
                  title={isPdf(d) ? "Preview this document" : "Download this file"}
                  data-testid={`tx-doc-open-${d.id}`}
                >
                  <div className="font-semibold text-slate-900 text-sm truncate flex items-center gap-2 hover:underline">
                    <span className="truncate">{d.name}</span>
                    <SignatureChip status={d.signature_status} testId={`tx-doc-sig-${d.id}`} />
                    {d.filled_from_document_id && (
                      <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-widest">
                        filled
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {d.mime_type || "unknown"} · {d.size_bytes ? `${Math.round(d.size_bytes/1024)} KB` : "—"} · added {new Date(d.created_at).toLocaleDateString()}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {isPdf(d) && !d.signature_status && (
                    <button
                      type="button"
                      onClick={() => setFillDoc({ id: d.id, name: d.name })}
                      className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md px-2 py-1 text-xs font-semibold"
                      title="Smart Fill this contract with the deal's data"
                      data-testid={`tx-doc-fill-${d.id}`}
                    >
                      <Wand2 className="h-3.5 w-3.5" /> Smart Fill
                    </button>
                  )}
                  {isPdf(d) && (
                    <button
                      type="button"
                      onClick={() => sendForSignature(d.id)}
                      disabled={sendingId === d.id || d.signature_status === "sent" || d.signature_status === "completed"}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: "var(--primary-hex)" }}
                      title={d.signature_status === "sent" ? "Already awaiting signature" : "Send to DocuSign"}
                      data-testid={`tx-doc-send-${d.id}`}
                    >
                      {sendingId === d.id ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</>
                      ) : (
                        <><Send className="h-3.5 w-3.5" /> Send to DocuSign</>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => download(d.id)}
                    className="text-slate-500 hover:text-slate-900 p-1"
                    title="Download"
                    data-testid={`tx-doc-dl-${d.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => armed ? deleteDoc(d.id) : setConfirmDelete(d.id)}
                    onBlur={() => armed && setConfirmDelete(null)}
                    className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${armed ? "bg-red-600 text-white" : "text-slate-400 hover:text-red-700 hover:bg-slate-100"}`}
                    title={armed ? "Tap again to confirm" : "Delete document"}
                    data-testid={`tx-doc-delete-${d.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {d.signed_document_id && renderSignedCopy(d.signed_document_id)}
            </li>
          );
        })}
      </ul>

      {pickerOpen && (
        <AddFromVaultModal
          txId={txId}
          onClose={() => setPickerOpen(false)}
          onAdded={() => { setPickerOpen(false); load(); }}
        />
      )}

      {fillDoc && (
        <PdfFillDialog
          docId={fillDoc.id}
          docName={fillDoc.name}
          onClose={() => setFillDoc(null)}
          onFilled={() => load()}
          onSendForSignature={(newDocId) => sendForSignature(newDocId)}
        />
      )}

      {previewDoc && (
        <DocPreviewModal
          docId={previewDoc.id}
          name={previewDoc.name}
          onClose={() => setPreviewDoc(null)}
          testIdPrefix="tx-doc-preview"
        />
      )}
    </div>
  );
}

function AddFromVaultModal({ txId, onClose, onAdded }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [assigningId, setAssigningId] = useState(null);

  useEffect(() => {
    api.get("/form-templates")
      .then(({ data }) => setTemplates(data))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter(t => {
    if (!q.trim()) return true;
    return (t.name || "").toLowerCase().includes(q.trim().toLowerCase());
  });

  const assign = async (tplId) => {
    setAssigningId(tplId);
    try {
      const { data } = await api.post(`/form-templates/${tplId}/assign`, { transaction_id: txId });
      toast.success(data.reused ? "Already added to this deal" : "Copy added to this deal");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="add-from-vault-modal">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Master Library</div>
            <h3 className="font-heading text-xl font-bold text-slate-900">Add from Vault</h3>
            <p className="mt-1 text-xs text-slate-500">Pick a master template — we'll create a fresh, deal-scoped copy on this transaction.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="add-from-vault-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search templates by name…"
              className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus-brand"
              data-testid="add-from-vault-search"
            />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {loading && <div className="p-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading Vault…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              {templates.length === 0
                ? <>Your Vault is empty. <Link to="/documents" className="font-semibold text-slate-900 hover:underline">Upload a template →</Link></>
                : "No templates match your search."}
            </div>
          )}
          {filtered.map(t => (
            <button
              key={t.id}
              type="button"
              disabled={assigningId === t.id}
              onClick={() => assign(t.id)}
              className="w-full text-left px-5 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`vault-pick-${t.id}`}
            >
              <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate flex items-center gap-2">
                  {t.name}
                  {t.is_acroform && <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-widest">Smart-Fill</span>}
                  {t.is_public && <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded uppercase tracking-widest">iKEY</span>}
                </div>
                <div className="text-[11px] text-slate-500">{(t.fields || []).length} field{(t.fields || []).length === 1 ? "" : "s"}</div>
              </div>
              {assigningId === t.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                  <Plus className="h-3.5 w-3.5" /> Add
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
